/**
 * Screen routing by state, with three doors before the table: the browser has
 * to be able to hold a passkey, the player has to have one, and an invite link
 * shows the table before it asks for either.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { hasLeftTable, type GameId } from '@cards/shared'
import { useTable } from './socket/useTable'
import { connectionScreen, canAct } from './socket/connection'
import { OfflineNotice, TableClosedNotice } from './socket/ConnectionNotice'
import { buildView } from './table/view'
import { getGameUi } from './games/registry'
import { TableScreen } from './table/TableScreen'
import { Lobby } from './lobby/Lobby'
import { Home, type LiveTable } from './lobby/Home'
import { InviteShare } from './lobby/InviteShare'
import { TablePreview, takeJoinIntent } from './lobby/TablePreview'
import { SignIn } from './auth/SignIn'
import { CreateAccount } from './auth/CreateAccount'
import { LinkRedeem } from './auth/LinkRedeem'
import { UnsupportedBrowser } from './auth/UnsupportedBrowser'
import { webAuthnAvailability } from './auth/passkey'
import { getAccount, clearSession, api, type Account as AccountModel } from './auth/session'
import { reauthenticateSocket } from './socket/socket'
import { SettingsSheet } from './settings/SettingsSheet'
import { useFairness } from './fairness/useFairness'

// The screens a player opens from a menu rather than lands on. Between them
// they carry the match analysis, the replay engine and the deal verifier —
// about a third of the app's own code — and none of it is needed to draw the
// first screen, so it is its own chunk, fetched once the app is idle (below)
// so that opening any of them is still immediate.
const screens = {
  account: () => import('./auth/Account'),
  history: () => import('./history/MatchHistory'),
  replay: () => import('./history/MatchReplay'),
  fairness: () => import('./history/FairnessView'),
  handReview: () => import('./table/HandReview'),
}
const Account = lazy(() => screens.account().then((m) => ({ default: m.Account })))
const MatchHistory = lazy(() => screens.history().then((m) => ({ default: m.MatchHistory })))
const MatchReplay = lazy(() => screens.replay().then((m) => ({ default: m.MatchReplay })))
const FairnessView = lazy(() => screens.fairness().then((m) => ({ default: m.FairnessView })))
const HandReview = lazy(() => screens.handReview().then((m) => ({ default: m.HandReview })))

function prefetchScreens(): void {
  const all = () => Object.values(screens).forEach((load) => void load().catch(() => {}))
  const later = () => (typeof requestIdleCallback === 'function' ? requestIdleCallback(all, { timeout: 3000 }) : window.setTimeout(all, 1500))
  if (document.readyState === 'complete') later()
  else window.addEventListener('load', later, { once: true })
}

type Overlay =
  | 'none'
  | 'settings'
  | 'account'
  | 'history'
  | 'share'
  | 'fairness'
  /** The finished match, read back from the database. */
  | 'replay'
  /** The hand just played, read from the live snapshot. */
  | 'handReview'

interface Route {
  readonly kind: 'home' | 'invite' | 'link'
  readonly value: string
}

function readRoute(): Route {
  const path = location.pathname
  const invite = /^\/t\/([A-Za-z0-9]{4,8})\/?$/.exec(path)
  if (invite) return { kind: 'invite', value: invite[1].toUpperCase() }
  const link = /^\/link\/([A-Za-z0-9_-]{8,})\/?$/.exec(path)
  if (link) return { kind: 'link', value: link[1] }
  return { kind: 'home', value: '' }
}

export default function App(): JSX.Element {
  const availability = useMemo(() => webAuthnAvailability(), [])
  const [account, setAccount] = useState<AccountModel | null>(getAccount())
  const [route, setRoute] = useState<Route>(readRoute)
  const [creating, setCreating] = useState(false)
  // Why the player is looking at the sign-in screen, when they did not ask to
  // be. A revoked or expired session is the server's verdict and has to be
  // said out loud, or signing in again looks like the app forgot them.
  const [signedOutNotice, setSignedOutNotice] = useState<string | null>(null)

  useEffect(() => {
    const onPop = () => setRoute(readRoute())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // The in-app-webview case is decided before anything is asked of the player.
  if (availability.kind !== 'ok') return <UnsupportedBrowser availability={availability} />

  if (route.kind === 'link') {
    return (
      <LinkRedeem
        token={route.value}
        onDone={(next) => {
          setAccount(next)
          reauthenticateSocket()
          history.replaceState(null, '', '/')
          setRoute({ kind: 'home', value: '' })
        }}
      />
    )
  }

  if (route.kind === 'invite' && !account) {
    return (
      <TablePreview
        code={route.value}
        signedIn={false}
        onJoin={(next) => {
          if (next) setAccount(next)
          reauthenticateSocket()
        }}
      />
    )
  }

  if (!account) {
    return creating ? (
      <div className="page">
        <div className="wordmark">
          cards{' '}
          <span className="suit">
            <span className="s-s">♠</span>
            <span className="s-h">♥</span>
            <span className="s-d">♦</span>
            <span className="s-c">♣</span>
          </span>
        </div>
        <div style={{ marginTop: 'auto', marginBottom: 'auto' }}>
          <CreateAccount
            heading="Make an account"
            blurb="It is a passkey and a name — no password, no email."
            cta="Create passkey"
            onDone={(next) => {
              setAccount(next)
              reauthenticateSocket()
            }}
          />
        </div>
      </div>
    ) : (
      <SignIn
        notice={signedOutNotice}
        onDone={(next) => {
          setSignedOutNotice(null)
          setAccount(next)
          reauthenticateSocket()
        }}
        onCreate={() => setCreating(true)}
      />
    )
  }

  return (
    <SignedIn
      account={account}
      route={route}
      onSignedOut={(notice) => {
        setSignedOutNotice(notice ?? null)
        setAccount(null)
      }}
    />
  )
}

function SignedIn({
  account,
  route,
  onSignedOut,
}: {
  account: AccountModel
  route: Route
  onSignedOut: (notice?: string) => void
}): JSX.Element {
  const table = useTable()
  const [overlay, setOverlay] = useState<Overlay>('none')
  useEffect(prefetchScreens, [])
  const [atHome, setAtHome] = useState(false)
  const [game, setGame] = useState<GameId>('five-hundred')

  const { snapshot, send, connection } = table
  const screen = connectionScreen(connection)
  // Left mid-match: the seat is still held, and the server still shows us the
  // table, but a reconnect is not a decision to go back to it. Home offers it
  // instead, and only "Resume" sits us down again.
  const walkedAway = snapshot !== null && hasLeftTable(snapshot.room, snapshot.you.userId)
  const home = atHome || walkedAway

  // A dead session cannot be recovered by waiting: the token no longer resolves
  // to a user, so the only honest move is back to the front door, carrying the
  // server's own reason.
  useEffect(() => {
    if (!connection.dead) return
    clearSession()
    onSignedOut(connection.dead.message)
  }, [connection.dead, onSignedOut])

  // An invite link that survived the passkey ceremony joins as soon as the
  // socket is up; the code is also still in the URL, which is the real fallback.
  const [joinIntent, setJoinIntent] = useState<string | null>(null)
  useEffect(() => {
    const intent = takeJoinIntent() ?? (route.kind === 'invite' ? route.value : null)
    if (intent) setJoinIntent(intent)
  }, [route])

  // Held rather than emitted straight away, because `send` now drops anything
  // written before there is a link instead of letting socket.io queue it. A
  // join is the one intent worth keeping across that wait: it is idempotent,
  // and it is what the player clicked a link to do.
  useEffect(() => {
    if (joinIntent === null || !canAct(connection)) return
    send.joinTable(joinIntent)
    setJoinIntent(null)
    history.replaceState(null, '', '/')
  }, [joinIntent, connection, send])

  // Records every commitment as it arrives — before the reveal can exist — and
  // then sends this device's own 32 bytes for its seat. Without the second half
  // every hand is dealt from server-chosen entropy alone.
  useFairness(snapshot)

  const view = useMemo(() => (snapshot ? buildView(snapshot) : null), [snapshot])
  const closeOverlay = useCallback(() => setOverlay('none'), [])

  // One of these at a time, in the order they matter: the session, the table,
  // then the signal. `signIn` draws nothing here — the effect above routes.
  const connectionNode =
    screen === 'ended' && connection.ended ? (
      <TableClosedNotice
        reason={connection.ended.reason}
        onLeave={() => {
          table.acknowledgeEnd()
          setAtHome(true)
        }}
      />
    ) : screen === 'offline' ? (
      <OfflineNotice connection={connection} />
    ) : null

  const overlayNode = (() => {
    switch (overlay) {
      case 'settings':
        return (
          <SettingsSheet
            onClose={closeOverlay}
            onAccount={() => setOverlay('account')}
            onLeave={
              snapshot && !home
                ? () => {
                    send.leaveTable()
                    setAtHome(true)
                    closeOverlay()
                  }
                : undefined
            }
            midMatch={snapshot?.room.status === 'playing'}
          />
        )
      case 'account':
        return <Account account={account} onBack={closeOverlay} onSignedOut={onSignedOut} />
      case 'history':
        return <MatchHistory game={game} onBack={closeOverlay} />
      case 'replay':
        // "Replay the match" means *this* match. It used to open the list of
        // every match instead, which was the only place the replay screens
        // could be reached from and was not what the button says.
        //
        // It reads the database, so it is only ever offered once the match is
        // over and the record has been written. The hand summary's "Review the
        // hand" pointed here too and got "No such match" every time; it opens
        // `handReview` below instead.
        return snapshot?.match ? (
          <MatchReplay matchId={snapshot.match.matchId} onBack={closeOverlay} />
        ) : (
          <MatchHistory game={game} onBack={closeOverlay} />
        )
      case 'handReview':
        return view ? <HandReview view={view} onBack={closeOverlay} /> : null
      case 'share':
        return snapshot ? <InviteShare code={snapshot.room.code} gameName={getGameUi(snapshot.room.gameId).name} onClose={closeOverlay} /> : null
      case 'fairness':
        return (
          <FairnessView match={snapshot?.match ?? null} onClose={closeOverlay} />
        )
      default:
        return null
    }
  })()

  if (overlay === 'account' || overlay === 'history' || overlay === 'replay' || overlay === 'handReview') {
    return <Suspense fallback={null}>{overlayNode}</Suspense>
  }

  const showTable = view !== null && !home

  if (showTable && view) {
    const body =
      view.snapshot.room.status === 'lobby' ? (
        <Lobby
          view={view}
          send={send}
          onMenu={() => setOverlay('settings')}
          onShare={() => setOverlay('share')}
          onLeave={() => {
            send.leaveTable()
            setAtHome(true)
          }}
        />
      ) : (
        <TableScreen
          view={view}
          send={send}
          blocked={!canAct(connection)}
          onMenu={() => setOverlay('settings')}
          onReview={() => setOverlay('replay')}
          onReviewHand={() => setOverlay('handReview')}
          onFairness={() => setOverlay('fairness')}
          onLeave={() => {
            send.leaveTable()
            setAtHome(true)
          }}
        />
      )
    return (
      <>
        {body}
        <Suspense fallback={null}>{overlayNode}</Suspense>
        {connectionNode}
        {table.error && screen === 'none' ? (
          <div className="toast" onClick={table.clearError}>
            {table.error.message}
          </div>
        ) : null}
      </>
    )
  }

  const live: LiveTable | null = view
    ? {
        tableId: view.snapshot.room.tableId,
        code: view.snapshot.room.code,
        heading: 'Your table is waiting',
        sentence: liveSentence(view.snapshot.room.code, view),
      }
    : null

  return (
    <>
      <Home
        game={game}
        onGame={setGame}
        live={live}
        onResume={() => {
          if (walkedAway && snapshot) send.resume(snapshot.room.tableId)
          setAtHome(false)
        }}
        onJoin={(code) => {
          send.joinTable(code)
          setAtHome(false)
        }}
        onNewTable={() => {
          send.createTable(game)
          setAtHome(false)
        }}
        onSettings={() => setOverlay('settings')}
        onAllMatches={() => setOverlay('history')}
      />
      <Suspense fallback={null}>{overlayNode}</Suspense>
      {connectionNode}
    </>
  )
}

/** One sentence about the table you left running, in the lobby's own voice. */
function liveSentence(code: string, view: ReturnType<typeof buildView>): string {
  const hand = view.hand
  if (!hand) return `Table ${code} is waiting for the first deal.`
  const trick = (hand.tricks.length || 0) + 1
  const reading = view.gameUi.trackCaption(view)
  const partner = view.seats.find((s) => s.isPartner)
  // Head-to-head games (2-player Bulgarian Belot) have no partner to name.
  const side = partner ? `You and ${partner.name} are` : view.match && view.match.table.teams.every((t) => t.seats.length === 1) ? 'You are' : 'You and your partner are'
  return `Hand ${hand.index + 1}, trick ${trick}. ${reading}. ${side} on ${view.scores.us} to ${view.scores.them}.`
}
